/**
 * Async access to the demo / empty datasets.
 *
 * Why this file exists
 * -------------------
 * `data/demo/*` and `data/fixtures/*` are ~5k lines of fixtures that only
 * `VITE_DATA_MODE=mock|empty` ever needs. They were imported at module scope by
 * production components and hooks, so they landed in the production bundle for
 * every real user. Two things made that unavoidable:
 *
 *  1. `getDataMode()` read `import.meta.env[key]` with a *computed* key. Vite
 *     only substitutes static member accesses, so the mode stayed a runtime
 *     value and no `if (isMock)` branch was statically dead. (Fixed in
 *     `config/env.ts` — it now reads `import.meta.env.VITE_DATA_MODE` directly.)
 *  2. Even with a foldable flag, a top-level `import { mockX } from '@/data/…'`
 *     is unconditional: the module is in the graph whether the branch runs or
 *     not.
 *
 * Both are needed. This module fixes (2) by exposing every dataset behind a
 * dynamic `import()`, which Rollup emits as its own chunk. In a production
 * build the mode folds to `'api'`, the callers below are unreachable, and the
 * chunk is never requested — and with `manualChunks` in `vite.config.ts` it is
 * a separate file, so it is not merely unexecuted but unfetched.
 *
 * Rules
 * -----
 * - Never `import` a value from `data/demo/*` or `data/fixtures/*` outside this
 *   file. `.eslintrc.cjs`'s `NO_MOCK_DATA` pattern enforces it for
 *   `components/`, `hooks/`, `contexts/` and `pages/`.
 * - Types are fine anywhere: `import type` is erased and costs nothing. Shared
 *   domain types live in `types/`, not in the fixtures — see
 *   `types/learning-data.ts`.
 * - Call these from inside a React Query `queryFn`, which is already async, so
 *   the await costs no extra render pass.
 */
import type {
  GraphData,
  LearningItem,
  LearningTopic,
  SearchHistory,
} from '@/types/learning-data';

export interface HomeDataset {
  searchHistory: SearchHistory[];
  suggestedTopics: LearningTopic[];
  getUserSearchHistory: (userId: string) => SearchHistory[];
  getRandomSuggestedTopics: (count: number) => LearningTopic[];
  isFirstTimeUser: (userId: string) => boolean;
}

export async function loadMockHomeData(): Promise<HomeDataset> {
  const m = await import('./demo/mockLearningData');
  return {
    searchHistory: m.mockSearchHistory,
    suggestedTopics: m.mockSuggestedTopics,
    getUserSearchHistory: m.getUserSearchHistory,
    getRandomSuggestedTopics: m.getRandomSuggestedTopics,
    isFirstTimeUser: m.isFirstTimeUser,
  };
}

export async function loadEmptyHomeData(): Promise<HomeDataset> {
  const m = await import('./demo/emptyData');
  return {
    searchHistory: m.emptySearchHistory,
    suggestedTopics: m.emptySuggestedTopics,
    getUserSearchHistory: () => m.emptySearchHistory,
    getRandomSuggestedTopics: () => m.emptySuggestedTopics,
    isFirstTimeUser: m.isFirstTimeUserEmpty,
  };
}

export async function loadMockGraphData(): Promise<GraphData> {
  const m = await import('./demo/mockGraphData');
  return m.mockGraphData;
}

export async function loadEmptyGraphData(): Promise<GraphData> {
  const m = await import('./demo/emptyData');
  return m.emptyGraphData;
}

export async function loadMockMyLearningData(): Promise<LearningItem[]> {
  const m = await import('./demo/mockMyLearningData');
  return m.mockMyLearningData;
}

export async function loadEmptyMyLearningData(): Promise<LearningItem[]> {
  const m = await import('./demo/emptyData');
  return m.emptyMyLearningData;
}

// ── Whole-module loaders ────────────────────────────────────────────────────
//
// The datasets below are consumed as several named builders rather than one
// value, so the loader returns the module namespace instead of enumerating
// every export. Same effect for bundling — one dynamic `import()`, one chunk —
// without this file having to track each builder's signature.
//
// `typeof import(...)` keeps the return fully typed, so callers still get
// completion and a compile error if a builder is renamed.

/** `mockPracticeData` — daily plan, practice queue, highlights feed, graph. */
export async function loadMockPracticeData(): Promise<
  typeof import('./demo/mockPracticeData')
> {
  return import('./demo/mockPracticeData');
}

/** `mockProgressData` — progress periods, lesson progress, insights. */
export async function loadMockProgressData(): Promise<
  typeof import('./demo/mockProgressData')
> {
  return import('./demo/mockProgressData');
}

/** `mockConceptDetails` — per-concept detail fixtures. */
export async function loadMockConceptDetails(): Promise<
  typeof import('./demo/mockConceptDetails')
> {
  return import('./demo/mockConceptDetails');
}

/** `mockMindspaceData` — knowledge archive, practice arena, quick bytes. */
export async function loadMockMindspaceData(): Promise<
  typeof import('./demo/mockMindspaceData')
> {
  return import('./demo/mockMindspaceData');
}

/** `mockLearningData` — home-page search history, topics, first-run check. */
export async function loadMockLearningData(): Promise<
  typeof import('./demo/mockLearningData')
> {
  return import('./demo/mockLearningData');
}

/** `mockWellbeingThoughts` — a few thoughts for today, for mock mode. */
export async function loadMockWellbeingThoughts(): Promise<
  typeof import('./demo/mockWellbeingThoughts')
> {
  return import('./demo/mockWellbeingThoughts');
}
